import React, { FC, useEffect, useState } from 'react'
import { Modal, Button, Spinner } from 'react-bootstrap'
import './pmsNestedModalAboveCompleteJob.css'
import { Link } from 'react-router-dom'
import { getRiskLibraryTask } from '../../../QHSE/core/_requests'
import type { RiskLibraryTaskDetail } from '../../../QHSE/core/_models'
import { useQhseRouteAccess } from '../../../QHSE/core/menuAccess'
import { QHSE_PERMS } from '../../../QHSE/core/permissions'
import { useAuth } from '../../../auth'
import { RiskLibraryTaskDetailReadOnlyView } from '../../../QHSE/components/RiskAssessmentLibrary/RiskLibraryTaskDetailReadOnlyView'
import {
  formatRiskLibraryDateOnly,
  formatRiskLibraryTaskVersion,
} from '../../../QHSE/components/RiskAssessmentLibrary/riskLibraryTaskDetailDisplay'

type Props = {
  visible: boolean
  taskId: number | null
  onClose: () => void
}

export const RiskLibraryTaskDetailModal: FC<Props> = ({ visible, taskId, onClose }) => {
  const { can } = useAuth()
  const { canEdit: canEditLibraryRoute } = useQhseRouteAccess('riskAssessmentLibrary')
  const permUpdate = can(QHSE_PERMS.RISK_LIB_TASK_UPDATE)
  const showEditLink = canEditLibraryRoute && permUpdate

  const [detail, setDetail] = useState<RiskLibraryTaskDetail | null>(null)
  const [loading, setLoading] = useState(false)
  const [err, setErr] = useState<string | null>(null)

  useEffect(() => {
    if (!visible || taskId == null) {
      setDetail(null)
      setErr(null)
      return
    }
    let cancelled = false
    setLoading(true)
    setErr(null)
    getRiskLibraryTask(taskId)
      .then((d) => {
        if (!cancelled) setDetail(d)
      })
      .catch((e: any) => {
        if (!cancelled) setErr('Failed to load task')
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [visible, taskId])

  return (
    <Modal
      show={visible}
      onHide={onClose}
      size='xl'
      scrollable
      className='pms-nested-above-complete-job'
      backdropClassName='pms-nested-above-complete-job-backdrop'
    >
      <Modal.Header closeButton>
        <Modal.Title>
          {detail ? (
            <>
              Task {detail.categoryTaskNumber}: {detail.title}{' '}
              <span
                className={
                  detail.globalDefault
                    ? 'badge badge-light-primary ms-2'
                    : 'badge badge-light-warning text-dark ms-2'
                }
              >
                {detail.globalDefault ? 'Default' : 'Custom'}
              </span>
              <span className='ms-2 text-muted fs-7'>v{formatRiskLibraryTaskVersion(detail.taskVersion)}</span>
              {detail.updatedAt && (
                <span className='ms-2 text-muted fs-8 d-block'>
                  Updated {formatRiskLibraryDateOnly(detail.updatedAt)}
                </span>
              )}
            </>
          ) : (
            'Task detail'
          )}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {loading && (
          <div className='text-center py-4'>
            <Spinner animation='border' size='sm' />
          </div>
        )}
        {err && <div className='text-danger'>{err}</div>}
        {detail && !loading && <RiskLibraryTaskDetailReadOnlyView detail={detail} />}
        {showEditLink && detail && !loading && (
          <div className='mt-3'>
            <Link
              to='/qhse/risk-assessment'
              className='btn btn-sm btn-light-primary'
              target='_blank'
              rel='noreferrer'
            >
              Edit in Risk Assessment library
            </Link>
          </div>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant='secondary' onClick={onClose}>
          Close
        </Button>
      </Modal.Footer>
    </Modal>
  )
}
